import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import React, { useEffect, useContext } from "react";
import Header from "./Header";
import LandingPage from "./LandingPage";
import OrgAdminPage from "./admin";
import AllChats from "./AllChats";
import LoginPage from "./LoginPage";
import WorkflowDetails from "./WorkflowDetails";
import AnalysisView from "./AnalysisView";
import Analyses from "./Analyses";
import AboutPage from "./pages/AboutPage";
import DesignerTutorial from "./DesignerTutorial";
import NfcPage from "./nfcPage";
import OffTheShelf from "./OffTheShelf";
import OtsLaunch from "./OtsLaunch";
import BYOP from "./byop";
import PipelineConfig from "./PipelineConfig";
import PipelineLaunch from "./PipelineLaunch";
import UserData from "./UserData";
import NfcoreLauncher from "./nfcoreLauncher";
import LoginRedirect from "./auth/loginRedirect";
import RequestAccess from "./auth/requestAccess";
import OrgRegisterDetails from "./OrgRegister";
import InviteRegistration from "./InviteRegistration";
import TermsPage from "./legal/TermsPage";
import PrivacyPage from "./legal/PrivacyPage";
import PublicDataImportPage from "./PublicDataImportPage";
import DownloadRequests from "./DownloadRequests";
import { SRA_CONFIG, GDC_CONFIG, TG_CONFIG } from "./publicDataConfigs";
import { AuthProvider, AuthContext } from "./AuthContext";
import API_BASE from "./config";
import "react-data-grid/lib/styles.css";
import PublicDataPage from "./PublicDataPage";
import UcscImportPage from "./UcscImportPage";
import EnsemblImportPage from "./EnsemblImportPage";
import CustomUrlImportPage from "./CustomUrlImportPage";
import NotebookPage from "./NotebookPage";
import RequireAuth from "./RequireAuth";   // ✅ NEW
import ClientPortal from "./clientportal/ClientPortal";
import ClientRegister from "./ClientRegister";

function AppContent() {
    const { userData, isAuthenticated, loading, logout, refresh } = useContext(AuthContext);

    // NOTE: initial session validation (and the silent-refresh fallback) lives
    // in AuthContext's init effect — don't duplicate it here or the two checks
    // race and can log out a session that AuthContext just refreshed.

    // -------------------------------
    // 1. GLOBAL BACKGROUND SESSION CHECK (every 1 minute)
    // -------------------------------
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(async () => {
            try {
                const res = await fetch(`${API_BASE}/auth/session`, {
                    method: "GET",
                    credentials: "include"
                });
                const data = await res.json().catch(() => ({}));

                // If the idToken looks expired/invalid, try one silent refresh
                // (the refresh token may still be valid) before signing out.
                // Only a failed refresh means the Cognito session is truly over.
                if (!res.ok || !data.authenticated) {
                    const refreshed = await refresh();
                    if (!refreshed) {
                        logout("Your session has expired. Please log in again.");
                    }
                }
            } catch (err) {
                const refreshed = await refresh();
                if (!refreshed) {
                    logout("Your session has expired. Please log in again.");
                }
            }
        }, 60 * 1000);

        return () => clearInterval(interval);
    }, [isAuthenticated, logout, refresh]);

    // -------------------------------
    // 2. IDLE TIMEOUT (30 minutes inactivity)
    // -------------------------------
    useEffect(() => {
        if (!isAuthenticated) return;

        let idleTimer;

        const resetIdleTimer = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                logout("You were logged out due to inactivity.");
            }, 30 * 60 * 1000);
        };

        window.addEventListener("mousemove", resetIdleTimer);
        window.addEventListener("keydown", resetIdleTimer);
        window.addEventListener("click", resetIdleTimer);
        window.addEventListener("scroll", resetIdleTimer);

        resetIdleTimer();

        return () => {
            window.removeEventListener("mousemove", resetIdleTimer);
            window.removeEventListener("keydown", resetIdleTimer);
            window.removeEventListener("click", resetIdleTimer);
            window.removeEventListener("scroll", resetIdleTimer);
            clearTimeout(idleTimer);
        };
    }, [isAuthenticated, logout]);

    // -------------------------------
    // 3. WAIT FOR AUTH LOADING
    // -------------------------------
    if (loading) {
        return (
            <div style={{ padding: "2rem", fontSize: "1.2rem" }}>
                Loading…
            </div>
        );
    }

    // ISOLATION: users in the Cognito 'client' group get ONLY the minimal client
    // portal — the full application (Header + all routes below) is never rendered
    // for them. /client-register stays reachable for the invited (not-yet-logged-in)
    // client. Backend enforces the same boundary via denyClientGroup + requireGroup.
    const isClient = isAuthenticated && (userData?.userGroups || []).includes("client");
    if (isClient) {
        return (
            <Routes>
                <Route path="/client-register" element={<ClientRegister />} />
                <Route path="/*" element={<ClientPortal userData={userData} onLogout={() => logout()} />} />
            </Routes>
        );
    }

    return (
        <>
            <Header
                showButtons={isAuthenticated}
                isAdmin={userData?.isAdmin}
                userData={userData}
                handleLogout={() => logout()}
            />

            <Routes>
                {/* PUBLIC ROUTES */}
                <Route
                    path="/login"
                    element={<LoginPage />}
                />
                <Route
                    path="/about"
                    element={<AboutPage />}
                />
                <Route
                    path="/designer-tutorial"
                    element={<DesignerTutorial />}
                />
                <Route
                    path="/request-access"
                    element={<RequestAccess />}
                />
                <Route
                    path="/org-register/details"
                    element={<OrgRegisterDetails />}
                />
                <Route
                    path="/org-invite/:token"
                    element={<InviteRegistration />}
                />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route
                    path="/client-register"
                    element={<ClientRegister />}
                />
                <Route
                    path="/public-data"
                    element={<PublicDataPage />}
                />

                {/* LANDING PAGE */}
                <Route
                    path="/"
                    element={
                        isAuthenticated
                            ? <LandingPage userData={userData} />
                            : <LoginPage />
                    }
                />

                {/* PROTECTED ROUTES */}
                <Route
                    path="/admin"
                    element={
                        <RequireAuth>
                            {userData?.isAdmin ? <OrgAdminPage /> : <LoginRedirect />}
                        </RequireAuth>
                    }
                />

                <Route
                    path="/all-chats"
                    element={
                        <RequireAuth>
                            <AllChats />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/workflow/:id"
                    element={
                        <RequireAuth>
                            <WorkflowDetails />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/analyses"
                    element={
                        <RequireAuth>
                            <Analyses />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/analysis/:workflowId"
                    element={
                        <RequireAuth>
                            <AnalysisView />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/analysisView"
                    element={
                        <RequireAuth>
                            <AnalysisView />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/nfcore"
                    element={
                        <RequireAuth>
                            <NfcPage />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/off-the-shelf"
                    element={
                        <RequireAuth>
                            <OffTheShelf />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/off-the-shelf/pipeline/:id"
                    element={
                        <RequireAuth>
                            <OtsLaunch />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/byop"
                    element={
                        <RequireAuth>
                            <BYOP />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/pipeline-config/:pipelineId"
                    element={
                        <RequireAuth>
                            <PipelineConfig />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/launch/:pipelineId"
                    element={
                        <RequireAuth>
                            <PipelineLaunch />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/user-data"
                    element={
                        <RequireAuth>
                            <UserData />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/nfcoreLauncher"
                    element={
                        <RequireAuth>
                            <NfcoreLauncher />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/import-sra"
                    element={
                        <RequireAuth>
                            <PublicDataImportPage config={SRA_CONFIG} />
                        </RequireAuth>
                    }
                />

                {/* Unified "Previous Downloads" across every public-data source. */}
                <Route
                    path="/downloads"
                    element={
                        <RequireAuth>
                            <DownloadRequests title="Previous Downloads" />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/import-gdc"
                    element={
                        <RequireAuth>
                            <PublicDataImportPage config={GDC_CONFIG} />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/import-1000g"
                    element={
                        <RequireAuth>
                            <PublicDataImportPage config={TG_CONFIG} />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/import-ucsc"
                    element={
                        <RequireAuth>
                            <UcscImportPage />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/import-ensembl"
                    element={
                        <RequireAuth>
                            <EnsemblImportPage />
                        </RequireAuth>
                    }
                />

                <Route
                    path="/import-url"
                    element={
                        <RequireAuth>
                            <CustomUrlImportPage />
                        </RequireAuth>
                    }
                />


                <Route
                    path="/notebook/:executionId/*"
                    element={
                        <RequireAuth>
                            <NotebookPage />
                        </RequireAuth>
                    }
                />
            </Routes>
        </>
    );
}

function App() {
    return (
        <AuthProvider>
            <Router>
                <AppContent />
            </Router>
        </AuthProvider>
    );
}

export default App;
